'use client';

import { useState } from 'react';
import { useStore } from 'zustand';
import { faPlus } from '@fortawesome/pro-light-svg-icons';
import { faCircleExclamation } from '@fortawesome/pro-solid-svg-icons';
import { DataTestIdEnum } from '@medprev-web-app/datatestid';

import { Button } from '@/components/ui/button/button.component';
import { Icon } from '@/components/ui/icon.component';
import { Typography } from '@/components/ui/typography/typography.component';
import { TypographyVariant } from '@/components/ui/typography/typography.type';
import { maskPhone } from '@/utils/formatters/phonenumber';
import { sumProcedurePrices } from '@/utils/procedures/procedures.util';
import { authStore } from '@/store/auth.store';
import { setOpenSignInModal } from '@/store/modal';
import { LoginOriginEnum } from '@/types/enums/analytics/login/login-analytics.enum';
import { getAnalyticsPlugin } from '@/app/(main)/_components/logger/analytics/get-analytics-plugin';
import { AnalyticsEnum } from '@/types/enums/analytics/analytics.enum';
import {
  PatientType,
  SearchCardPatientModal,
} from '@/app/(main)/(pages)/busca/[slug]/_components/search-schedules-list/search-card/_components/search-card-patient-modal.component';
import { SearchPartnerData } from '@/app/(main)/(pages)/busca/[slug]/_types/search-partner-data.type';

export function buildWarningMessage(phone: string): string {
  const suffix = phone ? ` ${phone}` : '';
  return `O agendamento deve estar previamente confirmado com o estabelecimento pelo número${suffix}.`;
}

interface SearchCardTabStandaloneProps {
  schedule: SearchPartnerData;
}

export function SearchCardTabStandalone({ schedule }: SearchCardTabStandaloneProps) {
  const [firstPhone] = schedule.partner.facility.contacts.phones;
  const phone = firstPhone ? maskPhone(firstPhone) : '';
  const warningMessage = buildWarningMessage(phone);

  const [isPatientModalOpen, setIsPatientModalOpen] = useState(false);
  const isLoggedIn = useStore(authStore, (state) => state.computed.isLoggedIn);
  const analytics = getAnalyticsPlugin();

  const partnerId = schedule.partner.id;
  const price = sumProcedurePrices(schedule.procedures);

  const addToCart = () => {
    analytics.track(AnalyticsEnum.STANDALONE_ADD_TO_CART, { partner_id: partnerId, price });
  };

  const startStandaloneFlow = () => {
    const hasDependents = Boolean(authStore.getState().authenticatedUser?.dependents?.length);
    if (hasDependents) {
      setIsPatientModalOpen(true);
      return;
    }
    addToCart();
  };

  const handleAddGuide = () => {
    analytics.track(AnalyticsEnum.CLICK_ADD_STANDALONE_GUIDE, { partner_id: partnerId });

    if (!isLoggedIn) {
      setOpenSignInModal(true, { origin: LoginOriginEnum.SEARCH_RESULT, loginCallback: startStandaloneFlow });
      return;
    }

    startStandaloneFlow();
  };

  const handleSelectPatient = (patientType: PatientType) => {
    analytics.track(AnalyticsEnum.STANDALONE_SELECT_PATIENT, { paciente_tipo: patientType });
    addToCart();
    setIsPatientModalOpen(false);
  };

  return (
    <div className="flex flex-col gap-2" data-testid={DataTestIdEnum.SEARCH_CARD_TAB_STANDALONE}>
      <Typography
        variant={TypographyVariant.MEDIUM_BODY_TEXT}
        className="font-semibold"
        data-testid={DataTestIdEnum.SEARCH_CARD_STANDALONE_DESCRIPTION}
      >
        Use esta opção para incluir exames esquecidos, complementares ou gerar uma guia de encaixe.
      </Typography>

      <div
        className="flex w-full items-start gap-3 rounded-2xl border border-warning-300 bg-warning-100 py-3 pl-3 pr-4 shadow-floating"
        data-testid={DataTestIdEnum.SEARCH_CARD_STANDALONE_WARNING}
      >
        <div className="flex size-6 shrink-0 items-center justify-center">
          <Icon icon={faCircleExclamation} className="text-xl text-warning-600" />
        </div>
        <p className="text-base font-semibold text-warning-800">{warningMessage}</p>
      </div>

      <Button
        startIcon={faPlus}
        startIconSize="1x"
        startIconClassName="text-base"
        className="py-2 text-base"
        data-testid={DataTestIdEnum.SEARCH_CARD_STANDALONE_ADD_BUTTON}
        onClick={handleAddGuide}
      >
        Adicionar guia avulsa
      </Button>

      <SearchCardPatientModal
        open={isPatientModalOpen}
        onOpenChange={setIsPatientModalOpen}
        onSelectPatient={handleSelectPatient}
      />
    </div>
  );
}
